import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Copy, CopyCheck, ExternalLink, IdCard } from 'lucide-react';
import { useEffect, useState } from 'react';
import backend from '../../../services/backend';
import { useCertificado } from '../../../services/hooks/useCertificado';
import formatValue from '../../../utils/formatValue';
import InputText from '../../shared/Inputs/InputText';

const CopyOpen = ({ funcao, copiando, link }) => {
  const color = useColorModeValue('#2D3748', 'white');
  const inputBorderColor = useColorModeValue<any>('gray.200', 'gray.600');
  return (
    <Flex
      justify="center"
      align="center"
      borderWidth="1px"
      borderLeftRadius="8px"
    >
      <Tooltip label="Copiar">
        <Flex
          borderLeftRadius="8px"
          h="100%"
          w="100%"
          cursor="pointer"
          _hover={{ bg: 'rgba(0,0,0,0.3)' }}
          justify="center"
          align="center"
          px="2"
          onClick={funcao}
        >
          {copiando ? <CopyCheck color={color} /> : <Copy color={color} />}
        </Flex>
      </Tooltip>
      <Flex w="2px" h="100%" bg={inputBorderColor} />
      <Tooltip label="Abrir em nova janela">
        <Flex
          h="100%"
          w="100%"
          cursor="pointer"
          _hover={{ bg: 'rgba(0,0,0,0.3)' }}
          justify="center"
          align="center"
          px="2"
          onClick={() => window.open(link, '_blank')}
        >
          <ExternalLink color={color} />
        </Flex>
      </Tooltip>
    </Flex>
  );
};

interface ICertificadoModal {
  isOpen: boolean;
  close(): void;
  idCertificado: string;
}

const CertificadoModal: React.FC<ICertificadoModal> = ({
  isOpen,
  close,
  idCertificado,
}) => {
  // estilos
  const color = useColorModeValue('#2D3748', 'white');
  const bg = useColorModeValue<any>('white', 'gray.800');
  const inputBorderColor = useColorModeValue<any>('gray.200', 'gray.600');
  const toast = useToast();

  // dados
  const { data, isLoading } = useCertificado(idCertificado);

  // estados
  const [agendamentoCopiado, setAgendamentoCopiado] = useState(false);
  const [pagamentoCopiado, setPagamentoCopiado] = useState(false);
  const [boletoCopiado, setBoletoCopiado] = useState(false);

  if (isLoading) {
    return (
      <Modal size="xl" isOpen={isOpen} onClose={close} bg={bg}>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader color={color}>Detalhes da Venda</ModalHeader>
          <ModalCloseButton color={color} />
          <Flex p="8" justify="center" align="center">
            <Spinner color="blue.500" />
          </Flex>
        </ModalContent>
      </Modal>
    );
  }

  const AgendamentoCopy = () => {
    setPagamentoCopiado(false);
    setBoletoCopiado(false);
    setAgendamentoCopiado(true);
    navigator.clipboard.writeText(data?.certcontrol?.links?.agendamento);
    setTimeout(() => {
      setAgendamentoCopiado(false);
    }, 5000);
  };
  const PagamentoCopy = () => {
    setAgendamentoCopiado(false);
    setBoletoCopiado(false);
    setPagamentoCopiado(true);
    navigator.clipboard.writeText(data?.certcontrol?.links?.pagamento?.url);
    setTimeout(() => {
      setPagamentoCopiado(false);
    }, 5000);
  };
  const BoletoCopy = () => {
    setAgendamentoCopiado(false);
    setPagamentoCopiado(false);
    setBoletoCopiado(true);
    navigator.clipboard.writeText(data?.certcontrol?.links?.pagamento?.boleto);
    setTimeout(() => {
      setBoletoCopiado(false);
    }, 5000);
  };

  const EmiteCertificado = async () => {
    window.open('https://emissor.ca.inf.br/prod/Emissor.jnlp');
    // try {
    //   const response = await backend.post(
    //     `/emite-certificado/${idCertificado}`,
    //   );
    //   toast({
    //     description: response?.data?.message,
    //     status: 'info',
    //     isClosable: true,
    //     position: 'bottom-right',
    //   });
    // } catch (error) {
    //   toast({
    //     title: 'Erro',
    //     status: 'error',
    //     isClosable: true,
    //     position: 'bottom-right',
    //   });
    // }
  };

  return (
    <Modal size="4xl" isOpen={isOpen} onClose={close} bg={bg}>
      <ModalOverlay>
        <ModalContent borderRadius="xl">
          <ModalHeader color={color}>
            Detalhes da Venda - {data?.certcontrol.pedido}
          </ModalHeader>
          <ModalCloseButton color={color} />
          <ModalBody>
            <Tabs>
              <TabList>
                <Tab color={color}>PEDIDO</Tab>
                <Tab color={color}>CERTIFICADO</Tab>
                <Tab color={color}>FATURA</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <>
                    <InputText
                      label="Produto"
                      labelColor={color}
                      value={data?.certcontrol?.produto?.descrição}
                    />
                    <Flex mt={2} />
                    <InputText
                      label="Parceiro"
                      labelColor={color}
                      value={data?.certificado.vendedor.nome}
                    />
                    <Flex mt={2} />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 2fr']}
                      gap={4}
                    >
                      <InputText
                        label="CPF/CNPJ"
                        labelColor={color}
                        value={data?.certificado.documento_certificado}
                      />
                      <InputText
                        label="Cliente"
                        labelColor={color}
                        value={data?.certificado.razao_nome}
                      />
                    </SimpleGrid>
                    <Flex mt={2} />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr']}
                      gap={4}
                    >
                      <InputText
                        label="Tipo Lançamento"
                        labelColor={color}
                        value={data?.certcontrol.tipo}
                      />
                      <InputText
                        label="Tipo Pagamento"
                        labelColor={color}
                        value={data?.certificado.tipo_pagamento ?? '-'}
                      />
                    </SimpleGrid>
                    <Flex mt={2} />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                    >
                      <InputText
                        label="Protocolo Soluti"
                        labelColor={color}
                        value={data?.certcontrol.soluti?.protocolo}
                      />
                      <InputText
                        label="Solicitação Soluti"
                        labelColor={color}
                        value={data?.certcontrol.soluti?.solicitação}
                      />
                      <InputText
                        label="Situação Soluti"
                        labelColor={color}
                        value={data?.certcontrol.soluti?.situação}
                      />
                    </SimpleGrid>
                    <Flex mt={2} />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                    >
                      <InputText
                        label="Data da Venda"
                        labelColor={color}
                        value={data?.certcontrol.data_venda}
                      />
                      <InputText
                        label="Data Pagamento"
                        labelColor={color}
                        value={data?.certcontrol.data_pagamento}
                      />
                      <InputText
                        label="Valor Total da Venda"
                        labelColor={color}
                        value={formatValue(data?.certcontrol.valor_total)}
                      />
                    </SimpleGrid>
                    <Flex w="100%" bg={inputBorderColor} h="2px" my="4" />
                    <InputText
                      label="Link Videoconferencia"
                      labelColor={color}
                      value={data?.certcontrol.links?.agendamento}
                      inputLeftAddon={
                        <CopyOpen
                          funcao={AgendamentoCopy}
                          copiando={agendamentoCopiado}
                          link={data?.certcontrol.links?.agendamento}
                        />
                      }
                    />
                    {/* <InputText
                      label="Boleto Pagamento"
                      labelColor={color}
                      value={data?.certcontrol.links?.pagamento?.boleto}
                      inputLeftAddon={
                        <CopyOpen
                          funcao={BoletoCopy}
                          copiando={boletoCopiado}
                          link={data?.certcontrol.links?.pagamento?.boleto}
                        />
                      }
                    /> */}
                    {/* <InputText
                      label="Resumo Pagamento"
                      labelColor={color}
                      value={data?.certcontrol.links?.pagamento?.url}
                      inputLeftAddon={
                        <CopyOpen
                          funcao={PagamentoCopy}
                          copiando={pagamentoCopiado}
                          link={data?.certcontrol.links?.pagamento?.url}
                        />
                      }
                    /> */}
                  </>
                </TabPanel>

                <TabPanel>
                  <>
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 2fr']}
                      gap={4}
                    >
                      <InputText
                        label="CPF/CNPJ"
                        labelColor={color}
                        value={data?.certificado.documento_certificado}
                      />
                      <InputText
                        label="Nome/Razão"
                        labelColor={color}
                        value={data?.certificado.razao_nome}
                      />
                    </SimpleGrid>
                    <SimpleGrid
                      mt={2}
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                    >
                      <InputText
                        label="CEI/CEAPF"
                        labelColor={color}
                        value={data?.certificado.cei_ceapf || ''}
                        placeholder=""
                      />
                      <InputText
                        label="CPF Rep. Legal"
                        labelColor={color}
                        value={data?.certificado.cpf_representante || ''}
                        placeholder=""
                      />
                      <InputText
                        label="Nome Rep. Legal"
                        labelColor={color}
                        value={data?.certificado.representante || ''}
                        placeholder=""
                      />
                    </SimpleGrid>
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                      mt={2}
                    >
                      <InputText
                        label="Celular"
                        labelColor={color}
                        value={data?.certificado.celular}
                      />
                      <InputText
                        label="Telefone"
                        labelColor={color}
                        value={data?.certificado.telefone}
                      />
                      <InputText
                        label="Email"
                        labelColor={color}
                        value={data?.certificado.email}
                      />
                    </SimpleGrid>
                    <Flex h="2px" w="100%" bg="gray.500" my="3" />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                      rowGap={2}
                      mb={2}
                    >
                      <InputText
                        label="Logradouro"
                        labelColor={color}
                        value={
                          data?.certificado.endereco_certificado.logradouro
                        }
                      />
                      <InputText
                        label="Número"
                        labelColor={color}
                        value={data?.certificado.endereco_certificado.numero}
                      />
                      <InputText
                        label="Bairro"
                        labelColor={color}
                        value={data?.certificado.endereco_certificado.bairro}
                      />
                      <InputText
                        label="Cidade"
                        labelColor={color}
                        value={data?.certificado.endereco_certificado.cidade}
                      />
                      <InputText
                        label="UF"
                        labelColor={color}
                        value={data?.certificado.endereco_certificado.uf}
                      />
                      <InputText
                        label="CEP"
                        labelColor={color}
                        value={data?.certificado.endereco_certificado.cep}
                      />
                    </SimpleGrid>
                    <InputText
                      label="Complemento"
                      labelColor={color}
                      value={data?.certificado.endereco_certificado.complemento}
                    />
                  </>
                </TabPanel>

                <TabPanel>
                  <>
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 2fr']}
                      gap={4}
                    >
                      <InputText
                        label="CPF/CNPJ"
                        labelColor={color}
                        value={data?.certificado.fatura.documento_fatura}
                      />
                      <InputText
                        label="Nome/Razão"
                        labelColor={color}
                        value={data?.certificado.fatura.razao_nome}
                      />
                      <InputText
                        label="Telefone"
                        labelColor={color}
                        value={data?.certificado.fatura.telefone}
                      />
                      <InputText
                        label="Email"
                        labelColor={color}
                        value={data?.certificado.fatura.email}
                      />
                    </SimpleGrid>
                    <Flex h="2px" w="100%" bg="gray.500" my="3" />
                    <SimpleGrid
                      templateColumns={['1fr', '1fr', '1fr 1fr 1fr']}
                      gap={4}
                      rowGap={2}
                      mb={2}
                    >
                      <InputText
                        label="Logradouro"
                        labelColor={color}
                        value={
                          data?.certificado.fatura.endereco_fatura.logradouro
                        }
                      />
                      <InputText
                        label="Número"
                        labelColor={color}
                        value={data?.certificado.fatura.endereco_fatura.numero}
                      />
                      <InputText
                        label="Bairro"
                        labelColor={color}
                        value={data?.certificado.fatura.endereco_fatura.bairro}
                      />
                      <InputText
                        label="Cidade"
                        labelColor={color}
                        value={data?.certificado.fatura.endereco_fatura.cidade}
                      />
                      <InputText
                        label="UF"
                        labelColor={color}
                        value={data?.certificado.fatura.endereco_fatura.uf}
                      />
                      <InputText
                        label="CEP"
                        labelColor={color}
                        value={data?.certificado.fatura.endereco_fatura.cep}
                      />
                    </SimpleGrid>
                  </>
                </TabPanel>
              </TabPanels>
            </Tabs>
            <Flex justify="flex-end">
              <Button leftIcon={<IdCard />} onClick={EmiteCertificado}>
                Emitir Certificado
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default CertificadoModal;
